import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { getEffectiveRole } from '../utils/roles';

/**
 * ProtectedRoute component to protect routes based on permissions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string} props.requiredPermission - Single permission code required (e.g., 'leads.view')
 * @param {string[]} props.requiredPermissions - Array of permission codes (user needs ANY of these)
 * @param {string[]} props.requiredAllPermissions - Array of permission codes (user needs ALL of these)
 * @param {string[]} props.allowedRoles - Array of roles that can access (legacy support)
 * @param {boolean} props.requireSuperAdmin - If true, only Super Admin can access (legacy support)
 * @param {boolean} props.requireAdmin - If true, only Admin or Super Admin can access (legacy support)
 * @param {boolean} props.requireManager - If true, Team Manager, Admin, or Super Admin can access (legacy support)
 */
const ProtectedRoute = ({ 
    children,
    requiredPermission,
    requiredPermissions = [],
    requiredAllPermissions = [],
    // Legacy role-based checks (kept for backward compatibility)
    allowedRoles = [], 
    requireSuperAdmin = false,
    requireAdmin = false,
    requireManager = false
}) => {
    const { user } = useAuth();
    const { hasPermission, hasAnyPermission, hasAllPermissions, isSuperAdmin } = usePermissions();
    // Always compare against the effective role so a user whose role only
    // lives in `roleId.name` (dynamic roles system) is not misclassified.
    const effectiveRole = getEffectiveRole(user);

    // If no user, redirect to login (but wait for auth to finish loading)
    // Note: This check happens in App.js, so if we reach here, user should exist
    // But we check anyway for safety
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Priority 1: Check permission-based access (new system)
    if (requiredPermission) {
        if (!hasPermission(requiredPermission)) {
            return <Navigate to="/403" replace />;
        }
    }

    if (requiredPermissions.length > 0) {
        if (!hasAnyPermission(requiredPermissions)) {
            return <Navigate to="/403" replace />;
        }
    }

    if (requiredAllPermissions.length > 0) {
        if (!hasAllPermissions(requiredAllPermissions)) {
            return <Navigate to="/403" replace />;
        }
    }

    // Priority 2: Legacy role-based checks (for backward compatibility)
    if (requireSuperAdmin && !isSuperAdmin()) {
        return <Navigate to="/403" replace />;
    }

    if (requireAdmin && effectiveRole !== 'super_admin' && effectiveRole !== 'admin') {
        return <Navigate to="/403" replace />;
    }

    if (requireManager && !hasPermission('projects.view') && !hasPermission('users.view') && !isSuperAdmin()) {
        return <Navigate to="/403" replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(effectiveRole)) {
        return <Navigate to="/403" replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;

